import styled, { css } from 'styled-components';
import { Icon } from 'src/components';

export const Page = styled.main(
  ({ theme }) => css`
    position: relative;
    background-color: ${theme.co.primary99};
    color: ${theme.co.secondary20};
    height: 100dvh;
    width: 100%;
    padding: ${theme.sz.s6};
    box-sizing: border-box;
    display: flex;
    align-items: safe center;
    justify-content: center;
    overflow: auto;

    @media (max-width: ${theme.sz.lg}) {
      padding: ${theme.sz.s4};
    }
  `,
);

export const Stage = styled.div(
  ({ theme }) => css`
    position: absolute;
    inset: 0;
    z-index: ${theme.zi.z1};
    overflow: hidden;
    background: ${theme.co.secondary20};
    pointer-events: none;
  `,
);

export const Clip = styled.video(
  () => css`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  `,
);

export const MuteButton = styled.button(
  ({ theme }) => css`
    position: absolute;
    z-index: ${theme.zi.z2};
    left: ${theme.sz.s4};
    bottom: ${theme.sz.s4};
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${theme.sz.s10};
    height: ${theme.sz.s10};
    padding: 0;
    border: none;
    border-radius: ${theme.bo.elliptical};
    background: ${theme.co.secondary20};
    color: ${theme.co.neutral99};
    cursor: pointer;

    && svg {
      width: ${theme.sz.s5};
      height: ${theme.sz.s5};
    }

    &:hover {
      background: ${theme.co.secondary10};
    }

    ${theme.focusVisible};
  `,
);

export const Panel = styled.div(
  ({ theme }) => css`
    position: relative;
    z-index: ${theme.zi.z2};
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s4};
    width: 100%;
    max-width: ${theme.sz.m};
    max-height: 100%;
    overflow-y: auto;
    padding: ${theme.sz.s6} ${theme.sz.s5} ${theme.sz.s5};
    border-radius: ${theme.bo.mediumLarge};
    background: ${theme.co.primary99};
    box-shadow: ${theme.sh.s4};
  `,
);

export const Headline = styled.h1(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.s};
    color: ${theme.co.secondary20};
    margin: ${theme.sz.s0};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.s};
    }
  `,
);

export const Subhead = styled.p(
  ({ theme }) => css`
    ${theme.typography.paragraph.base};
    color: ${theme.co.secondary40};
    margin: ${theme.sz.s0};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.paragraph.m};
    }
  `,
);

export const PlanList = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s3};
  `,
);

export const PlanOption = styled.button<{ $selected: boolean }>(
  ({ theme, $selected }) => css`
    position: relative;
    display: flex;
    align-items: center;
    gap: ${theme.sz.s4};
    width: 100%;
    padding: ${theme.sz.s4} ${theme.sz.s5};
    border-radius: ${theme.bo.medium};
    border: 2px solid ${theme.co.secondary90};
    background: ${theme.co.neutral99};
    cursor: pointer;
    text-align: start;
    transition:
      border-color 150ms ease,
      background-color 150ms ease;

    &:hover {
      border-color: ${theme.co.primary50};
    }

    ${
      $selected &&
      css`
        border-color: ${theme.co.primary20};
        background: ${theme.co.primary98};

        &:hover {
          border-color: ${theme.co.primary20};
        }
      `
    }
  `,
);

export const Radio = styled.span<{ $selected: boolean }>(
  ({ theme, $selected }) => css`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${theme.sz.s6};
    height: ${theme.sz.s6};
    border-radius: ${theme.bo.elliptical};
    border: 2px solid ${$selected ? theme.co.secondary20 : theme.co.secondary70};
    background: ${$selected ? theme.co.secondary20 : theme.co.neutral99};
    color: ${theme.co.neutral99};
  `,
);

export const RadioCheck = styled(Icon)(
  ({ theme }) => css`
    width: ${theme.sz.s4};
    height: ${theme.sz.s4};
  `,
);

export const PlanLabel = styled.span(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xs};
    color: ${theme.co.secondary20};
    flex: 1;
    text-align: start;

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.xs};
    }
  `,
);

export const PlanPrice = styled.span(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xs};
    color: ${theme.co.secondary20};
    font-variant-numeric: tabular-nums;

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.xs};
    }
  `,
);

export const Actions = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.sz.s3};
    width: 100%;
  `,
);

export const ContinueForFree = styled.button(
  ({ theme }) => css`
    ${theme.typography.paragraph.base};
    align-self: center;
    padding: ${theme.sz.s2} ${theme.sz.s4};
    border: none;
    background: none;
    color: ${theme.co.secondary40};
    font-weight: 600;
    text-align: center;
    text-decoration: underline;
    text-underline-offset: 0.18em;
    cursor: pointer;

    &:hover {
      color: ${theme.co.secondary20};
    }

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.paragraph.m};
    }
  `,
);

export const PopularBadge = styled.span(
  ({ theme }) => css`
    position: absolute;
    top: -${theme.sz.s3};
    right: ${theme.sz.s4};
    padding: ${theme.sz.s1} ${theme.sz.s3};
    border-radius: ${theme.bo.small};
    background: ${theme.co.secondary20};
    color: ${theme.co.neutral99};
    ${theme.typography.paragraph.s};
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  `,
);
